import React from 'react';
import { Shield, Lock, FileText, Users, AlertCircle, Database, Network, Settings, CheckCircle, XCircle, Info } from 'lucide-react';

const ZykainPrivacyPolicy = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const StatusBadge = ({ status, text }) => {
    const config = {
      implemented: { icon: CheckCircle, colors: 'bg-green-100 text-green-800 border-green-200' },
      partial: { icon: Info, colors: 'bg-blue-100 text-blue-800 border-blue-200' },
      notApplicable: { icon: XCircle, colors: 'bg-gray-100 text-gray-600 border-gray-200' }
    };
    
    const { icon: Icon, colors } = config[status];

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${colors} whitespace-nowrap flex-shrink-0`}>
        <Icon className="h-3 w-3 mr-1 flex-shrink-0" />
        <span className="truncate">{text}</span>
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0">
              <a href="/" className="text-2xl text-gray-800 font-black tracking-wide">
                ZYKAIN
              </a>
            </div>
          </div>
        </div>
      </nav>

      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-xl p-8 mb-8">
          <div className="flex items-center justify-center mb-6">
            <Shield className="h-12 w-12 text-blue-600 mr-4" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">ZYKAIN</h1>
              <h2 className="text-xl font-semibold text-blue-600">Privacy Policy</h2>
            </div>
          </div>
          <div className="text-center text-gray-600">
            <p>Last Updated: {currentDate}</p>
            <p>Version: 2025.1</p>
          </div>
        </div>

        {/* Quick Navigation Overview */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Network className="h-5 w-5 mr-2 text-blue-600" />
            Quick Navigation
          </h3>
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
            <p className="text-blue-800">
              <strong>Privacy-First Approach:</strong> ZYKAIN is committed to protecting your privacy. 
              This policy explains how we collect, use, and safeguard your information when using our 
              applications, services, and website.
            </p>
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <a href="#privacy-overview" className="text-blue-600 hover:text-blue-700 font-medium">Overview</a>
            <a href="#data-collection" className="text-blue-600 hover:text-blue-700 font-medium">Data Collection</a>
            <a href="#data-usage" className="text-blue-600 hover:text-blue-700 font-medium">How We Use Data</a>
            <a href="#data-sharing" className="text-blue-600 hover:text-blue-700 font-medium">Data Sharing</a>
            <a href="#your-rights" className="text-blue-600 hover:text-blue-700 font-medium">Your Rights</a>
            <a href="#security" className="text-blue-600 hover:text-blue-700 font-medium">Security</a>
            <a href="#retention" className="text-blue-600 hover:text-blue-700 font-medium">Retention</a>
            <a href="#contact-privacy" className="text-blue-600 hover:text-blue-700 font-medium">Contact</a>
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          
          {/* Privacy Overview */}
          <section id="privacy-overview" className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Shield className="h-6 w-6 mr-2 text-blue-600" />
              1. Privacy Overview
            </h3>
            <div className="prose prose-blue max-w-none">
              <p className="text-gray-700 mb-4">
                ZYKAIN ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, disclose, and safeguard your information when you use our 
                applications, services, and website.
              </p>
              
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Key Privacy Principles</h4>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>We collect only the minimum data necessary to provide our services</li>
                <li>We never sell your personal information to third parties</li>
                <li>We implement industry-standard security measures to protect your data</li>
                <li>You have control over your data and can request deletion at any time</li>
              </ul>
            </div>
          </section>

          {/* Information We Collect */}
          <section id="data-collection" className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Database className="h-6 w-6 mr-2 text-purple-600" />
              2. Information We Collect
            </h3>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-purple-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Personal Information</span>
                  <p className="text-sm text-gray-600">Name, email, company info when you contact us or sign up</p>
                </div>
                <StatusBadge status="implemented" text="Minimal Collection" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-purple-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Atlassian Forge App Data</span>
                  <p className="text-sm text-gray-600">App usage analytics, performance metrics, error logs</p>
                </div>
                <StatusBadge status="implemented" text="App Functionality Only" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-purple-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Technical Information</span>
                  <p className="text-sm text-gray-600">IP address, browser type, device info, usage patterns</p>
                </div>
                <StatusBadge status="implemented" text="Standard Web Analytics" />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-2">Data Collection Details:</h5>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• <strong>Personal Data:</strong> Only collected when you voluntarily provide it</li>
                  <li>• <strong>App Data:</strong> Follows Atlassian's strict data handling requirements</li>
                  <li>• <strong>No Content Access:</strong> We never access your Atlassian content without permission</li>
                  <li>• <strong>Analytics:</strong> Standard web analytics for service improvement</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Information */}
          <section id="data-usage" className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Settings className="h-6 w-6 mr-2 text-green-600" />
              3. How We Use Your Information
            </h3>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-green-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Service Provision</span>
                  <p className="text-sm text-gray-600">Provide apps, process requests, deliver support</p>
                </div>
                <StatusBadge status="implemented" text="Core Business" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-green-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Improvement & Analytics</span>
                  <p className="text-sm text-gray-600">Analyze usage, improve products, develop features</p>
                </div>
                <StatusBadge status="implemented" text="Product Enhancement" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-green-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Legal & Security</span>
                  <p className="text-sm text-gray-600">Comply with obligations, protect against fraud</p>
                </div>
                <StatusBadge status="implemented" text="Compliance" />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-2">Usage Purposes:</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                  <div>
                    <strong>Service Operations:</strong>
                    <ul className="list-disc list-inside mt-1">
                      <li>Provide and maintain applications</li>
                      <li>Process requests and communications</li>
                      <li>Deliver customer support</li>
                    </ul>
                  </div>
                  <div>
                    <strong>Business Improvement:</strong>
                    <ul className="list-disc list-inside mt-1">
                      <li>Analyze usage patterns</li>
                      <li>Develop new features</li>
                      <li>Conduct research and development</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Data Sharing */}
          <section id="data-sharing" className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Network className="h-6 w-6 mr-2 text-red-600" />
              4. Data Sharing and Disclosure
            </h3>
            <div className="space-y-4">
              <div className="bg-red-50 border-l-4 border-red-400 p-4">
                <h5 className="font-medium text-red-800 mb-2">We Do Not Sell Your Data</h5>
                <p className="text-red-700 text-sm">
                  ZYKAIN does not sell, rent, or trade your personal information to third parties for marketing purposes.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Service Providers</span>
                  <p className="text-sm text-gray-600">Trusted vendors for hosting, analytics, customer support</p>
                </div>
                <StatusBadge status="partial" text="Limited & Necessary" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Legal Requirements</span>
                  <p className="text-sm text-gray-600">When required by law, court order, or government request</p>
                </div>
                <StatusBadge status="partial" text="Legal Compliance Only" />
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h5 className="font-medium text-blue-800 mb-2">Atlassian Marketplace Compliance</h5>
                <p className="text-blue-700 text-sm">
                  Our Forge apps comply with Atlassian's data residency and privacy requirements. Data processed 
                  through Atlassian products remains within Atlassian's infrastructure and is subject to Atlassian's privacy policies.
                </p>
              </div>
            </div>
          </section>

          {/* Your Rights */}
          <section id="your-rights" className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="h-6 w-6 mr-2 text-indigo-600" />
              5. Your Privacy Rights
            </h3>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-indigo-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Access & Portability</span>
                  <p className="text-sm text-gray-600">Request a copy of your personal information</p>
                </div>
                <StatusBadge status="implemented" text="Available" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-indigo-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Correction & Deletion</span>
                  <p className="text-sm text-gray-600">Update or delete your personal information</p>
                </div>
                <StatusBadge status="implemented" text="Available" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-indigo-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Opt-Out</span>
                  <p className="text-sm text-gray-600">Unsubscribe from marketing communications</p>
                </div>
                <StatusBadge status="implemented" text="Always Available" />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-2">How to Exercise Your Rights:</h5>
                <p className="text-sm text-gray-700">
                  To exercise any of these rights, contact us at <a href="mailto:hello@zykain.com" className="text-blue-600 hover:text-blue-700 underline">hello@zykain.com</a>. 
                  We will respond to your request within 30 days.
                </p>
              </div>
            </div>
          </section>

          {/* Security & Retention */}
          <section id="security" className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Lock className="h-6 w-6 mr-2 text-yellow-600" />
              6. Data Security
            </h3>
            <div className="space-y-4">
              <p className="text-gray-700">
                We implement appropriate technical and organizational security measures to protect your personal information:
              </p>
              
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h5 className="font-medium text-yellow-800 mb-2">Security Measures:</h5>
                <ul className="text-yellow-700 text-sm space-y-1">
                  <li>• Encryption in transit and at rest</li>
                  <li>• Access controls and authentication</li>
                  <li>• Regular security assessments</li>
                  <li>• Employee privacy training</li>
                  <li>• Incident response procedures</li>
                </ul>
              </div>
            </div>
          </section>

          <section id="retention" className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <AlertCircle className="h-6 w-6 mr-2 text-orange-600" />
              7. Data Retention
            </h3>
            <div className="space-y-4">
              <p className="text-gray-700">
                We retain your personal information only as long as necessary to provide our services, 
                comply with legal obligations, resolve disputes, and enforce our agreements.
              </p>
              
              <div className="bg-orange-50 p-4 rounded-lg">
                <h5 className="font-medium text-orange-800 mb-2">Data Lifecycle:</h5>
                <p className="text-orange-700 text-sm">
                  When data is no longer needed, we securely delete or anonymize it according to our data retention schedule.
                </p>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section id="contact-privacy" className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-6 w-6 mr-2 text-blue-600" />
              8. Contact Us About Privacy
            </h3>
            <div className="prose prose-blue max-w-none">
              <p className="text-gray-700 mb-4">
                For privacy-related inquiries, data requests, or questions about this policy, 
                please contact us using the following information:
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Privacy Contact</h4>
                <ul className="space-y-2 text-gray-700">
                  <li><strong>Email:</strong> <a href="mailto:hello@zykain.com" className="text-blue-600 hover:text-blue-700 underline">hello@zykain.com</a></li>
                  <li><strong>Company:</strong> ZYKAIN</li>
                  <li><strong>Location:</strong> Remote-first company based out of Delhi-NCR, India</li>
                  <li><strong>Response Time:</strong> We typically respond within 24-48 hours</li>
                </ul>
              </div>
            </div>
          </section>

        </div>

        {/* Final Note (before footer) */}
        <div className="bg-white p-6 mt-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center">
              <p className="text-gray-600 text-sm mb-2">
                This privacy policy reflects our current data handling practices and commitment to protecting 
                your privacy in accordance with applicable privacy laws and regulations.
              </p>
              <p className="text-gray-500 text-xs">
                © 2025 ZYKAIN Privacy Policy. All rights reserved.
              </p>
            </div>
            
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800 text-sm">
                <strong>Note:</strong> Our apps leverage Atlassian's Forge platform for enhanced security and 
                privacy controls. Many privacy protections are inherited from Atlassian's infrastructure.
              </p>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="text-white py-12" style={{ backgroundColor: '#16163F' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-2xl font-black tracking-wide mb-4">ZYKAIN</h3>
            <p className="text-gray-400 mb-6">Smart Apps, Smarter Solutions</p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-3">
              <a
                href="/terms-of-service"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Terms of Service
              </a>
              <a 
                href="/sprint-reporter-faq" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                FAQ
              </a>
              <a 
                href="/support" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                Support
              </a>
              <a 
                href="/security-policy" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                Security Policy
              </a>
              <a 
                href="/sla-policy" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                Service Level Policy
              </a>
              <a
                href="/#contact"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Contact
              </a>
            </div>
            <div className="mt-8 pt-8 border-t border-gray-800">
              <p className="text-gray-500 text-sm">
                © 2025 ZYKAIN. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ZykainPrivacyPolicy;